import { Loader } from "sui";
import { z } from "zod";

import { i18n } from "@lib/i18n";
import { queries } from "@lib/queries";
import { globalLayoutRoute } from "@routes";
import { allowAdminAndUser, redirectToAccessFallback, requireScope } from "@routes/authorization";
import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";

import styles from "./TaskForm.module.css";

export const taskFormRoute = createRoute({
    getParentRoute: () => globalLayoutRoute,
    path: `taskPosting/$taskId`,
    component: lazyRouteComponent(() => import("./index")),
    beforeLoad: async ({ params, context }) => {
        const allowedAdminAndUser = await allowAdminAndUser();
        if (!allowedAdminAndUser) {
            redirectToAccessFallback();
        }
        requireScope("account");

        const task = await context.queryClient.fetchQuery(queries.task.detail(params.taskId));
        if (task?.status !== "draft") {
            throw redirect({ to: "/tasks/view/$taskId", params: { taskId: params.taskId } });
        }

        return { getTitle: () => i18n.doc_title_posting() };
    },
});

export const motiveStepRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `motive`,
    component: lazyRouteComponent(() => import("./MotiveStep/MotiveStep"), "MotiveStep"),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});

export const planningTypeStepRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `planning-type`,
    component: lazyRouteComponent(
        () => import("./PlanningTypeStep/PlanningTypeStep"),
        "PlanningTypeStep",
    ),
    beforeLoad: async (args) => {
        const task = await args.context.queryClient.fetchQuery(
            queries.task.detail(args.params.taskId),
        );
        // Do not allow to go to the planning type step if the task has shifts
        // Because Flexible Planning shifts cannot be mixed with regular shifts
        if (task?.shifts?.length > 0) {
            throw redirect({
                to: !task.flexiblePlanning
                    ? "/taskPosting/$taskId/shifts"
                    : "/taskPosting/$taskId/flexible-planning",
                params: {
                    taskId: args.params.taskId,
                },
            });
        }
    },
});

export const flexiblePlanningStepRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `flexible-planning`,
    component: lazyRouteComponent(
        () => import("./FlexiblePlanningStep/FlexiblePlanningStep"),
        "FlexiblePlanningStep",
    ),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});

const shiftStepSearchSchema = z
    .object({
        create: z.boolean().optional(),
    })
    .partial();
type ShiftStepSearch = z.infer<typeof shiftStepSearchSchema>;

export const shiftsStepRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `shifts`,
    component: lazyRouteComponent(() => import("./ShiftsStep/ShiftsStep"), "ShiftsStep"),
    validateSearch: (search: ShiftStepSearch) => shiftStepSearchSchema.parse(search),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});

export const detailsStepRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `details`,
    component: lazyRouteComponent(() => import("./DetailsStep/DetailsStep"), "DetailsStep"),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});

export const WorkersSelectionRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `workers`,
    component: lazyRouteComponent(() => import("./WorkersStep/WorkersStep"), "WorkersStep"),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});

export const summaryRoute = createRoute({
    getParentRoute: () => taskFormRoute,
    path: `summary`,
    component: lazyRouteComponent(() => import("./Summary"), "Summary"),
    pendingComponent: () => (
        <div className={styles.loadingLayout}>
            <Loader className='text-blue-500' />
        </div>
    ),
});
